import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Banknote, Trash2 } from 'lucide-react';
import WasteTracker from '../components/accounting/WasteTracker';
import ExportToolbar from '../components/accounting/ExportToolbar';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Calendar } from 'lucide-react';
import { useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CashWastePage() {
  const { token, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const branchId = activeBranch?.id;
  const [wasteData, setWasteData] = useState(null);

  const handleWasteData = useCallback((d) => { setWasteData(d); }, []);

  const buildReport = useCallback(() => {
    if (!wasteData) return null;
    const d = wasteData;
    const sar = t('common.sar');
    return {
      kpis: [
        { label: t('waste.totalCost'), value: d.total_waste_cost, unit: sar, highlight: 'negative' },
        { label: t('waste.totalEntries'), value: d.total_entries },
        { label: t('waste.pctRevenue'), value: `${d.waste_pct_of_revenue}%`, highlight: d.waste_pct_of_revenue > 3 ? 'negative' : '' },
      ],
      sections: [
        {
          type: 'table', title: t('waste.byCategory'),
          headers: [t('accounting.category'), t('waste.cost'), t('waste.totalEntries')],
          rows: (d.by_category || []).map(c => ({
            cells: [
              { value: t(`waste.${c.category}`) },
              { value: `${c.total_cost} ${sar}`, type: 'num' },
              { value: c.count, type: 'num' },
            ]
          }))
        },
        {
          type: 'table', title: t('waste.topWasted'),
          headers: ['#', t('common.name'), t('waste.cost'), t('waste.totalEntries')],
          rows: (d.top_items || []).map((it, i) => ({
            cells: [
              { value: i + 1 },
              { value: it.name || '-' },
              { value: `${it.total_cost} ${sar}`, type: 'num', color: 'negative' },
              { value: it.count, type: 'num' },
            ]
          }))
        }
      ]
    };
  }, [wasteData, t]);

  return (
    <ModuleLayout moduleKey="finance">
      <div className="space-y-5" data-testid="cash-waste-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('nav.cashWaste')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{activeBranch?.name || ''}</p>
          </div>
          <ExportToolbar title={t('nav.cashWaste')} subtitle={activeBranch?.name} buildReport={buildReport} t={t} lang={lang} />
        </div>
        <Tabs defaultValue="waste" className="w-full">
          <TabsList className="w-full grid grid-cols-2 h-auto p-1 bg-muted/50" data-testid="cash-waste-tabs">
            <TabsTrigger value="waste" className="text-xs sm:text-sm py-2" data-testid="tab-waste">
              <Trash2 className="w-4 h-4 me-1.5" />{t('accounting.wasteTracker')}
            </TabsTrigger>
            <TabsTrigger value="cash" className="text-xs sm:text-sm py-2" data-testid="tab-cash">
              <Banknote className="w-4 h-4 me-1.5" />{t('accounting.cashMgmt')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="waste">
            <WasteTracker branchId={branchId} token={token} t={t} lang={lang} onSummaryReady={handleWasteData} />
          </TabsContent>
          <TabsContent value="cash">
            <CashMgmtInline branchId={branchId} token={token} t={t} lang={lang} />
          </TabsContent>
        </Tabs>
      </div>
    </ModuleLayout>
  );
}

function CashMgmtInline({ branchId, token, t, lang }) {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/accounting/cash-management?branch_id=${branchId || ''}&date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
        setShifts(data.shifts || []);
      } catch {}
      setLoading(false);
    };
    fetchData();
  }, [branchId, token, date]);

  if (loading) return <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="cash-mgmt-tab">
      <div className="flex items-center gap-3">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" />
      </div>
      {shifts.length === 0 ? (
        <div className="text-center py-12">
          <Banknote className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">{t('accounting.noShifts')}</p>
        </div>
      ) : (
        shifts.map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{s.opened_at?.slice(11, 16)} - {s.closed_at?.slice(11, 16) || '...'}</Badge>
                <Badge className={s.status === 'closed' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}>{s.status}</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><span className="text-muted-foreground text-xs">{t('accounting.openingCash')}</span><p className="font-medium">{fmt(s.opening_cash)}</p></div>
                <div><span className="text-muted-foreground text-xs">{t('accounting.cashSales')}</span><p className="font-medium">{fmt(s.cash_sales)}</p></div>
                <div><span className="text-muted-foreground text-xs">{t('accounting.expectedCash')}</span><p className="font-medium">{fmt(s.expected_cash)}</p></div>
                <div><span className="text-muted-foreground text-xs">{t('accounting.actualCash')}</span><p className="font-medium">{fmt(s.actual_cash)}</p></div>
                <div>
                  <span className="text-muted-foreground text-xs">{t('accounting.difference')}</span>
                  <p className={`font-medium ${(s.cash_difference || 0) === 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(s.cash_difference)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
